import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage, getLang, getLocale, getCurrencySymbol } from "../lang/useLanguage";
import PaginationBar from "../components/PaginationBar";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });

const fmt = (d) =>
  d
    ? new Date(d).toLocaleDateString(getLocale(), {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      })
    : "—";
const money = (v) =>
  v != null
    ? `${Number(v).toLocaleString(getLocale())} ${getCurrencySymbol()}`
    : "—";

const SALES_ROLES = [
  "sales","dis_ticaret_mudur","dis_ticaret_uzman","dis_ticaret_temsilci",
  "ic_satis_mudur","ic_satis_uzman","ic_satis_temsilci",
];
const MANAGER_ROLES = [
  "admin",
  "manager",
  "general_manager",
  "planning",
  "planning_manager",
  "production_manager",
  "dis_ticaret_mudur",
  "ic_satis_mudur"
];

const getOrderStatus = () => {
  const l = getLang();
  return {
    draft:            { label: l.salesOrders?.statusDraft,          color: "#94a3b8", bg: "rgba(148,163,184,0.12)" },
    pending_approval: { label: l.salesOrders?.statusPendingApproval,   color: "#fbbf24", bg: "rgba(251,191,36,0.12)"  },
    approved:         { label: l.salesOrders?.statusApproved,       color: "#60a5fa", bg: "rgba(96,165,250,0.12)"  },
    rejected:         { label: l.salesOrders?.statusRejected,      color: "#f87171", bg: "rgba(248,113,113,0.12)" },
    in_production:    { label: l.salesOrders?.statusInProduction,        color: "#a78bfa", bg: "rgba(167,139,250,0.12)" },
    qc_pending:       { label: l.salesOrders?.statusQcPending,      color: "#f97316", bg: "rgba(249,115,22,0.12)"   },
    qc_passed:        { label: l.salesOrders?.statusQcPassed,         color: "#10b981", bg: "rgba(16,185,129,0.12)"   },
    assembly_pending: { label: l.salesOrders?.statusAssemblyPending,  color: "#f59e0b", bg: "rgba(245,158,11,0.12)"   },
    assembly_in_progress: { label: l.salesOrders?.statusAssemblyInProgress,     color: "#3b82f6", bg: "rgba(59,130,246,0.12)"   },
    assembly_done:    { label: l.salesOrders?.statusAssemblyDone,color: "#8b5cf6", bg: "rgba(139,92,246,0.12)"   },
    ready:            { label: l.salesOrders?.statusReady, color: "#00ff9d", bg: "rgba(0,255,157,0.12)"   },
    shipped:          { label: l.salesOrders?.statusShipped,     color: "#34d399", bg: "rgba(52,211,153,0.12)"  },
    closed:           { label: l.salesOrders?.statusClosed,         color: "#475569", bg: "rgba(71,85,105,0.12)"   },
    completed:        { label: l.salesOrders?.statusCompleted,      color: "#059669", bg: "rgba(5,150,105,0.12)"   },
    cancelled:        { label: l.salesOrders?.statusCancelled,           color: "#ef4444", bg: "rgba(239,68,68,0.12)"   },
    partial_shipped:  { label: l.salesOrders?.statusPartialShipped,  color: "#f97316" },
  };
};

const StatusBadge = ({ status }) => {
  const ORDER_STATUS = getOrderStatus();
  const cfg = ORDER_STATUS[status] || ORDER_STATUS.draft;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}44`,
      letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
};

// ─── YENİ MÜŞTERİ FORMU ──────────────────────────────────────────────────
const NewCustomerForm = ({ onCreated }) => {
  const { t: theme } = useTheme();
  const { t: l } = useLanguage();
  const [form, setForm] = useState({
    customer_name: "", customer_type: "domestic", phone: "", email: "", city: "", address: "",
    assigned_sales_rep: "",
  });
  const [salesReps, setSalesReps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const userRole = localStorage.getItem("role") || "";
  const isSalesRep = ["ic_satis_temsilci", "dis_ticaret_temsilci"].includes(userRole);

  useEffect(() => {
    fetch(`${API}/users`, { headers: headers() })
      .then(r => r.ok ? r.json() : [])
      .then(users => setSalesReps(
        (Array.isArray(users) ? users : []).filter(u =>
          ["dis_ticaret_mudur","dis_ticaret_uzman","dis_ticaret_temsilci",
           "ic_satis_mudur","ic_satis_uzman","ic_satis_temsilci"].includes(u.role) && u.is_active
        )
      ));
  }, []);

  const t = theme;
  const inputStyle = {
    width: "100%", padding: "8px 12px", background: t.bg.input,
    border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary,
    fontSize: 13, outline: "none", boxSizing: "border-box",
  };
  const labelStyle = {
    display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5,
  };

  const handleSave = async () => {
    if (!form.customer_name) { setError(l.salesOrders?.errCustomerNameRequired); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/customers`, {
        method: "POST", headers: headers(), body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || (l.salesOrders?.errGeneric));
      const d = await res.json();
      onCreated({ ...form, id: d.id, customer_code: d.customer_code });
      setForm({ customer_name: "", customer_type: "domestic", phone: "", email: "", city: "", address: "", assigned_sales_rep: "" });
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div style={{
      marginTop: 10, padding: 14, background: t.bg.input,
      borderRadius: 10, border: `1px solid ${t.border.subtle}`,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: t.accent.blue, marginBottom: 10 }}>
        + {l.salesOrders?.newCustomer}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div style={{ gridColumn: "1/-1" }}>
          <label style={labelStyle}>{l.salesOrders?.customerName} *</label>
          <input type="text" value={form.customer_name}
            onChange={e => setForm(p => ({ ...p, customer_name: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>{l.salesOrders?.customerType}</label>
          <select value={form.customer_type} onChange={e => setForm(p => ({ ...p, customer_type: e.target.value }))} style={inputStyle}>
            <option value="domestic">{l.salesOrders?.domestic}</option>
            <option value="foreign">{l.salesOrders?.foreign}</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>{l.salesOrders?.city}</label>
          <input type="text" value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>{l.salesOrders?.phone}</label>
          <input type="text" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>{l.salesOrders?.email}</label>
          <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} style={inputStyle} />
        </div>
        {!isSalesRep && (
          <div style={{ gridColumn: "1/-1" }}>
            <label style={labelStyle}>{l.salesOrders?.assignedSalesRep}</label>
            <select value={form.assigned_sales_rep}
              onChange={e => setForm(p => ({ ...p, assigned_sales_rep: e.target.value }))}
              style={inputStyle}>
              <option value="">— {l.salesOrders?.unassigned} —</option>
              {salesReps.map(u => (
                <option key={u.id} value={u.username}>{u.username}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      {error && <div style={{ color: t.accent.red, fontSize: 12, marginBottom: 8 }}>{error}</div>}
      <button onClick={handleSave} disabled={loading} style={{
        padding: "8px 16px", borderRadius: 8, border: "none",
        background: "linear-gradient(135deg,#059669,#10b981)",
        color: "#fff", fontSize: 12, fontWeight: 600, cursor: loading ? "default" : "pointer",
      }}>
        {loading
          ? (l.salesOrders?.saving)
          : `✓ ${l.salesOrders?.saveCustomer}`}
      </button>
    </div>
  );
};

// ─── SİPARİŞ FORMU MODAL ─────────────────────────────────────────────────
const OrderFormModal = ({ existing, onClose, onSaved, t }) => {
  const { t: l } = useLanguage();
  const isEdit = !!existing;
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerSearch, setCustomerSearch] = useState(existing?.customer_name || "");
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [form, setForm] = useState({
    customer_id: existing?.customer_id || "",
    customer_name: existing?.customer_name || "",
    customer_type: existing?.customer_type || "domestic",
    order_date: existing?.order_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
    delivery_date: existing?.delivery_date?.slice(0, 10) || "",
    notes: existing?.notes || "",
  });
  const [items, setItems] = useState(
    existing?.items?.map(i => ({ ...i, showList: false })) ||
    [{ product_code: "", product_name: "", quantity: "", unit: "adet", unit_price: "", showList: false }]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/customers`, { headers: headers() })
      .then(r => r.ok ? r.json() : [])
      .then(c => setCustomers(Array.isArray(c) ? c : []))
      .catch(() => setCustomers([]));

    fetch(`${API}/product-cards?sales_only=1`, { headers: headers() })
      .then(r => r.ok ? r.json() : [])
      .then(p => {
        const list = Array.isArray(p) ? p : (p.products || p.items || []);
        setProducts(list.filter(x => Number(x.is_sales_item ?? 1) === 1 && Number(x.is_active ?? 1) === 1));
      })
      .catch(() => setProducts([]));
  }, []);

  const filteredCustomers = customers.filter(c =>
    c.customer_name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.customer_code?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const selectCustomer = (c) => {
    setCustomerSearch(c.customer_name);
    setForm(p => ({
      ...p, customer_id: c.id,
      customer_name: c.customer_name,
      customer_type: c.customer_type || "domestic",
    }));
    setShowCustomerList(false);
    setShowNewCustomer(false);
  };

  const updateItem = (idx, key, val) =>
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [key]: val } : item));

  const selectProduct = (idx, p) => {
    const alreadyAdded = items.some((item, i) => i !== idx && item.product_code === p.product_code);
    if (alreadyAdded) {
      setError(`"${p.product_name}" ${l.salesOrders?.alreadyInList}`);
      setTimeout(() => setError(""), 3000);
      return;
    }
    setItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, product_code: p.product_code, product_name: p.product_name, unit_price: p.sale_price || item.unit_price || "", showList: false } : item
    ));
  };

  const addItem = () => setItems(p => [
    ...p, { product_code: "", product_name: "", quantity: "", unit: "adet", unit_price: "", showList: false },
  ]);
  const removeItem = (idx) => { if (items.length > 1) setItems(p => p.filter((_, i) => i !== idx)); };

  const handleSave = async () => {
    if (!form.customer_name) { setError(l.salesOrders?.errSelectCustomer); return; }
    if (items.some(i => !i.product_code)) {
      setError(l.salesOrders?.errSelectFromList); return;
    }
    if (items.some(i => !i.product_name || !i.quantity)) {
      setError(l.salesOrders?.errAllFieldsRequired); return;
    }
    const codes = items.map(i => i.product_code || i.product_name).filter(Boolean);
    if (new Set(codes).size !== codes.length) { setError(l.salesOrders?.errDuplicateProduct); return; }
    setLoading(true); setError("");
    try {
      const body = {
        ...form,
        items: items.map(i => ({
          product_code: i.product_code,
          product_name: i.product_name,
          quantity: parseInt(i.quantity) || 0,
          unit: i.unit,
          unit_price: parseFloat(i.unit_price) || 0,
        })),
      };
      const url = isEdit ? `${API}/sales-orders/${existing.id}` : `${API}/sales-orders`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST", headers: headers(), body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).detail || (l.salesOrders?.errGeneric));
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inputStyle = {
    width: "100%", padding: "9px 12px", background: t.bg.input,
    border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary,
    fontSize: 13, outline: "none", boxSizing: "border-box",
  };
  const labelStyle = {
    display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay,
      backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20,
        width: "100%", maxWidth: 800, maxHeight: "92vh", overflow: "hidden",
        display: "flex", flexDirection: "column", boxShadow: t.shadow.modal,
      }} onClick={e => e.stopPropagation()}>

        <div style={{
          padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`,
          display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header,
        }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>
            {isEdit
              ? `✏️ ${l.salesOrders?.editOrder}`
              : `🛒 ${l.salesOrders?.newOrder}`}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 28, display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Müşteri */}
          <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.subtle}`, borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 12, textTransform: "uppercase" }}>
              👤 {l.salesOrders?.customerInfo}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div style={{ position: "relative", gridColumn: "1/-1" }}>
                <label style={labelStyle}>{l.salesOrders?.searchCustomer} *</label>
                <input type="text" value={customerSearch}
                  onChange={e => { setCustomerSearch(e.target.value); setShowCustomerList(true); }}
                  onFocus={() => setShowCustomerList(true)}
                  onBlur={() => setTimeout(() => setShowCustomerList(false), 150)}
                  placeholder={l.salesOrders?.customerSearchPlaceholder}
                  style={inputStyle} />
                {showCustomerList && filteredCustomers.length > 0 && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                    background: t.bg.modal, border: `1px solid ${t.border.default}`,
                    borderRadius: 8, maxHeight: 180, overflowY: "auto", boxShadow: t.shadow.modal, marginTop: 2,
                  }}>
                    {filteredCustomers.map(c => (
                      <div key={c.id} onClick={() => selectCustomer(c)} style={{
                        padding: "10px 14px", cursor: "pointer", fontSize: 13,
                        borderBottom: `1px solid ${t.border.subtle}`,
                        display: "flex", justifyContent: "space-between",
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = t.bg.rowAlt}
                        onMouseLeave={e => e.currentTarget.style.background = "none"}>
                        <span style={{ color: t.text.primary, fontWeight: 600 }}>{c.customer_name}</span>
                        <div style={{ display: "flex", gap: 8 }}>
                          <span style={{ fontSize: 11, color: t.text.muted }}>{c.customer_code}</span>
                          <span style={{
                            fontSize: 10, padding: "1px 6px", borderRadius: 20,
                            color: c.customer_type === "foreign" ? "#a78bfa" : "#60a5fa",
                            background: c.customer_type === "foreign" ? "rgba(167,139,250,0.15)" : "rgba(96,165,250,0.15)",
                          }}>
                            {c.customer_type === "foreign"
                              ? (l.salesOrders?.foreign)
                              : (l.salesOrders?.domestic)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>{l.salesOrders?.orderDate}</label>
                <input type="date" value={form.order_date}
                  onChange={e => setForm(p => ({ ...p, order_date: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>{l.salesOrders?.deliveryDate}</label>
                <input type="date" value={form.delivery_date}
                  onChange={e => setForm(p => ({ ...p, delivery_date: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>{l.salesOrders?.notes}</label>
                <input type="text" value={form.notes}
                  onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} style={inputStyle} />
              </div>
            </div>

            <button onClick={() => setShowNewCustomer(p => !p)} style={{
              marginTop: 10, padding: "6px 14px", borderRadius: 8, cursor: "pointer",
              border: `1px solid ${t.accent.blueBorder}`, background: showNewCustomer ? t.accent.blueBg : "none",
              color: t.accent.blue, fontSize: 12, fontWeight: 600,
            }}>
              {showNewCustomer
                ? `▲ ${l.salesOrders?.close}`
                : `+ ${l.salesOrders?.addNewCustomer}`}
            </button>

            {showNewCustomer && (
              <NewCustomerForm onCreated={(newC) => {
                setCustomers(p => [...p, newC]);
                selectCustomer(newC);
              }} />
            )}

            {form.customer_name && (
              <div style={{
                marginTop: 10, padding: "8px 12px", background: t.accent.blueBg,
                border: `1px solid ${t.accent.blueBorder}`, borderRadius: 8,
                fontSize: 12, color: t.accent.blue,
              }}>
                ✓ {l.salesOrders?.selected}: <strong>{form.customer_name}</strong>
                <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.7 }}>
                  ({form.customer_type === "foreign"
                    ? (l.salesOrders?.foreign)
                    : (l.salesOrders?.domestic)})
                </span>
              </div>
            )}
          </div>

          {/* Kalemler */}
          <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.subtle}`, borderRadius: 12, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>
                📦 {l.salesOrders?.orderItems}
              </div>
              <button onClick={addItem} style={{
                padding: "6px 14px", borderRadius: 8, cursor: "pointer", border: "none",
                background: "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", fontSize: 12, fontWeight: 600,
              }}>+ {l.salesOrders?.addItem}</button>
            </div>

            {items.map((item, idx) => {
              const filteredProds = products.filter(p => {
                const search = (item.product_name || "").toLowerCase().replace(/[-\s]/g, "");
                const name = (p.product_name || "").toLowerCase().replace(/[-\s]/g, "");
                const code = (p.product_code || "").toLowerCase().replace(/[-\s]/g, "");
                return name.includes(search) || code.includes(search);
              });
              return (
                <div key={idx} style={{
                  background: t.bg.row, borderRadius: 10, padding: 14,
                  marginBottom: 10, border: `1px solid ${t.border.subtle}`,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: t.text.muted }}>
                      {l.salesOrders?.item} {idx + 1}
                    </span>
                    {items.length > 1 && (
                      <button onClick={() => removeItem(idx)} style={{
                        background: "none", border: "none", color: t.accent.red, cursor: "pointer", fontSize: 16,
                      }}>✕</button>
                    )}
                  </div>
                  <div style={{ position: "relative" }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", marginBottom: 4 }}>
                      {l.salesOrders?.productName} *
                    </label>
                    <input type="text" value={item.product_name}
                      onChange={e => { updateItem(idx, "product_name", e.target.value); updateItem(idx, "product_code", ""); updateItem(idx, "showList", true); }}
                      onFocus={() => updateItem(idx, "showList", true)}
                      onBlur={() => setTimeout(() => updateItem(idx, "showList", false), 150)}
                      placeholder={l.salesOrders?.searchProduct}
                      style={{ ...inputStyle, borderColor: item.product_name && !item.product_code ? "#ef4444" : undefined }} />
                    {item.product_code && (
                      <div style={{ fontSize: 10, color: t.accent.blue, marginTop: 3, fontWeight: 600 }}>
                        📦 {item.product_code}
                      </div>
                    )}
                    {item.product_name && !item.product_code && (
                      <div style={{ fontSize: 10, color: "#ef4444", marginTop: 3, fontWeight: 600 }}>
                        ⚠️ {l.salesOrders?.selectFromListWarning}
                      </div>
                    )}
                    {item.showList && filteredProds.length > 0 && (
                      <div style={{
                        position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                        background: t.bg.modal, border: `1px solid ${t.border.default}`,
                        borderRadius: 8, maxHeight: 150, overflowY: "auto", boxShadow: t.shadow.modal, marginTop: 2,
                      }}>
                        {filteredProds.map(p => {
                          const alreadyAdded = items.some((it, i) => i !== idx && it.product_code === p.product_code);
                          return (
                            <div key={p.id || p.product_code}
                              onClick={() => !alreadyAdded && selectProduct(idx, p)}
                              style={{
                                padding: "8px 12px", cursor: alreadyAdded ? "default" : "pointer",
                                fontSize: 12, borderBottom: `1px solid ${t.border.subtle}`,
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                opacity: alreadyAdded ? 0.5 : 1,
                              }}
                              onMouseEnter={e => { if (!alreadyAdded) e.currentTarget.style.background = t.bg.rowAlt; }}
                              onMouseLeave={e => e.currentTarget.style.background = "none"}>
                              <span style={{ color: t.text.primary }}>{p.product_name}</span>
                              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                                  <span style={{
                                    fontSize: 11, fontWeight: 700,
                                    color: (p.available_qty || 0) > 0 ? "#00ff9d" : "#f87171",
                                    background: (p.available_qty || 0) > 0 ? "rgba(0,255,157,0.12)" : "rgba(248,113,113,0.1)",
                                    padding: "1px 6px", borderRadius: 10,
                                  }}>
                                    {(p.available_qty || 0) > 0
                                      ? `✅ ${l.salesOrders?.available}: ${p.available_qty}`
                                      : `⚠️ ${l.salesOrders?.noStock}`}
                                  </span>
                                  {(p.stock_qty || 0) > 0 && (p.available_qty || 0) === 0 && (
                                    <span style={{ fontSize: 10, color: "#fbbf24" }}>
                                      {l.salesOrders?.allReserved || `Depoda ${p.stock_qty} adet var ama tümü ayrılmış`}
                                    </span>
                                  )}
                                  {(p.stock_qty || 0) > 0 && (p.available_qty || 0) > 0 && (
                                    <span style={{ fontSize: 10, color: "#94a3b8" }}>
                                      {l.salesOrders?.total}: {p.stock_qty} · {l.salesOrders?.reserved}: {(p.stock_qty || 0) - (p.available_qty || 0)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", marginBottom: 4 }}>
                      {l.salesOrders?.quantity} *
                    </label>
                    <input type="number" value={item.quantity}
                      onChange={e => updateItem(idx, "quantity", e.target.value)}
                      style={inputStyle} min="1" />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", marginBottom: 4 }}>
                      {l.salesOrders?.unit}
                    </label>
                    <select value={item.unit} onChange={e => updateItem(idx, "unit", e.target.value)} style={inputStyle}>
                      <option value="adet">{l.salesOrders?.unitPcs}</option>
                      <option value="kg">KG</option>
                      <option value="mt">MT</option>
                      <option value="lt">LT</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", marginBottom: 4 }}>
                      {l.salesOrders?.unitPrice} ({getCurrencySymbol()})
                    </label>
                    <input type="number" value={item.unit_price}
                      onChange={e => updateItem(idx, "unit_price", e.target.value)}
                      style={inputStyle} min="0" step="0.01" />
                  </div>
                </div>
              );
            })}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
              <div style={{
                background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`,
                borderRadius: 8, padding: "8px 16px", fontSize: 13, color: t.accent.blue, fontWeight: 700,
              }}>
                {l.salesOrders?.total}: {money(items.reduce((s, i) => s + ((parseFloat(i.unit_price) || 0) * (parseInt(i.quantity) || 0)), 0))}
              </div>
            </div>
          </div>

          {error && (
            <div style={{
              padding: "10px 16px", background: t.accent.redBg,
              border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13,
            }}>⚠️ {error}</div>
          )}
        </div>

        <div style={{
          padding: "16px 28px", borderTop: `1px solid ${t.border.default}`,
          display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header,
        }}>
          <div style={{ fontSize: 12, color: t.text.muted }}>
            {items.length} {l.salesOrders?.item} · {items.reduce((s, i) => s + (parseInt(i.quantity) || 0), 0)} {l.salesOrders?.unitPcs} {l.salesOrders?.totalLabel}
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={onClose} style={{
              padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`,
              background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13,
            }}>{l.salesOrders?.cancel}</button>
            <button onClick={handleSave} disabled={loading} style={{
              padding: "10px 24px", borderRadius: 8, border: "none",
              background: loading ? t.bg.tag : "linear-gradient(135deg,#3b82f6,#6366f1)",
              color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 600,
            }}>
              {loading
                ? (l.salesOrders?.saving)
                : isEdit
                  ? `💾 ${l.salesOrders?.update}`
                  : `💾 ${l.salesOrders?.saveOrder}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── SİPARİŞ DETAY MODAL ─────────────────────────────────────────────────
const OrderDetailModal = ({ order, onClose, onRefresh, t, userRole, currentUser }) => {
  const effectiveUserRole = userRole || localStorage.getItem("role") || "";

  const { t: l } = useLanguage();
  const ORDER_STATUS = getOrderStatus();
  const [detail, setDetail] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  const canManageInvoice = [
    "admin",
    "general_manager",
    "deputy_general_manager",
    "accounting",
    "finance"
  ].includes((effectiveUserRole || "").toLowerCase());
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [productionResult, setProductionResult] = useState(null);

  const canApprove = MANAGER_ROLES.includes(userRole);
  const isSalesPerson = SALES_ROLES.includes(userRole) || userRole === "admin";
  const isOwnOrder = detail?.created_by === currentUser;
  const canEdit =
    isSalesPerson &&
    (detail?.status ?? order.status) === "draft";
  const canCancel = (isSalesPerson && isOwnOrder) || canApprove;

  useEffect(() => {
    fetch(`${API}/sales-orders/${order.id}`, { headers: headers() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setDetail(d))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));


    fetch(`${API}/invoices?order_no=${order.order_no}`, { headers: headers() })
      .then(r => r.ok ? r.json() : { invoices: [] })
      .then(d => setInvoiceHistory(Array.isArray(d) ? d : d.invoices || []))
      .catch(() => setInvoiceHistory([]));
  }, [order.id, order.order_no]);

  // P6F.6 — sales order action/error handling
  const doAction = async (url, body = {}) => {
    if (actionLoading) return false;

    setActionLoading(true);

    try {
      const res = await fetch(url, {
        method: "PATCH",
        headers: headers(),
        body: JSON.stringify(body)
      });

      let data = {};

      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (!res.ok) {
        alert(
          data.detail ||
          data.message ||
          l.salesOrders?.errGeneric ||
          "İşlem gerçekleştirilemedi"
        );
        return false;
      }

      onRefresh();
      onClose();
      return true;

    } catch (e) {
      alert(
        l.salesOrders?.errConnection ||
        "Sunucu bağlantısı kurulamadı"
      );
      return false;

    } finally {
      setActionLoading(false);
    }
  };

  const handleStartProduction = async () => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      const orderId = detail?.id || order?.id;
      if (!orderId) {
        alert(l.salesOrders?.errOrderIdMissing);
        setActionLoading(false);
        return;
      }

      const res = await fetch(`${API}/sales-orders/${orderId}/start-production`, {
        method: "PATCH", headers: headers(),
      });
      const data = await res.json();
      if (res.ok) {
        setProductionResult(data);
        onRefresh();
        onClose();
      } else {
        alert(data.detail || (l.salesOrders?.errGeneric));
      }
    } catch (e) {
      alert(l.salesOrders?.errConnection);
    }
    setActionLoading(false);
  };

  const handleCancel = async () => {
    if (actionLoading) return;

    if (!window.confirm(l.salesOrders?.confirmCancel)) return;

    const reasonInput = window.prompt(
      l.salesOrders?.cancelReasonPrompt
    );

    // Prompt Cancel seçilirse siparişi iptal etme.
    if (reasonInput === null) return;

    const orderId = detail?.id || order?.id;

    if (!orderId) {
      alert(
        l.salesOrders?.errOrderIdMissing ||
        "Sipariş kimliği bulunamadı"
      );
      return;
    }

    const reason =
      reasonInput.trim() ||
      l.salesOrders?.cancelled ||
      "Sipariş iptal edildi";

    await doAction(
      `${API}/sales-orders/${orderId}/approval`,
      {
        action: "cancel",
        rejection_reason: reason
      }
    );
  };


  const inputStyle = {
    width: "100%", padding: "8px 12px", background: t.bg.input,
    border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary,
    fontSize: 13, outline: "none", boxSizing: "border-box",
  };

  if (showEdit && detail) {
    return (
      <OrderFormModal
        existing={{ ...detail, items: detail.items }}
        onClose={() => setShowEdit(false)}
        onSaved={() => { setShowEdit(false); onRefresh(); onClose(); }}
        t={t}
      />
    );
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay,
      backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20,
        width: "100%", maxWidth: 900, maxHeight: "92vh", overflow: "hidden",
        display: "flex", flexDirection: "column", boxShadow: t.shadow.modal,
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`,
          display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>{order.order_no}</div>
              <div style={{ fontSize: 13, color: t.text.muted }}>{order.customer_name} · {fmt(order.order_date)}</div>
            </div>
            <StatusBadge status={order.status} />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {canEdit && (
              <button onClick={() => setShowEdit(true)} style={{
                padding: "8px 14px", borderRadius: 8,
                border: `1px solid ${t.accent.blueBorder}`, background: t.accent.blueBg,
                color: t.accent.blue, cursor: "pointer", fontSize: 12, fontWeight: 600,
              }}>✏️ {l.salesOrders?.edit}</button>
            )}
            {isSalesPerson && !canApprove && (detail?.status ?? order.status) === "draft" && (
              <button onClick={() => doAction(`${API}/sales-orders/${order.id}/approval`, { action: "submit" })}
                disabled={actionLoading} style={{
                  padding: "8px 16px", borderRadius: 8, border: "none",
                  background: "linear-gradient(135deg,#059669,#10b981)",
                  color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700,
                }}>📤 {l.salesOrders?.submitForApproval}</button>
            )}
            {isSalesPerson && !canApprove && (detail?.status ?? order.status) === "pending_approval" && (
              <button disabled style={{
                padding: "8px 16px", borderRadius: 8, border: "1px solid #f59e0b44",
                background: "rgba(245,158,11,0.1)", color: "#f59e0b", cursor: "not-allowed", fontSize: 12, fontWeight: 700,
              }}>⏳ {l.salesOrders?.adminApprovalPending}</button>
            )}
            {canApprove && (detail?.status ?? order.status) === "pending_approval" && (
              <span style={{
                padding: "4px 10px", borderRadius: 20, border: "1px solid #f59e0b44",
                background: "rgba(245,158,11,0.1)", color: "#f59e0b", fontSize: 11, fontWeight: 700,
                whiteSpace: "nowrap",
              }}>
                ⏳ {l.salesOrders?.approvalByUser?.replace(
              "{user}",
              detail?.created_by || "—"
            )}
              </span>
            )}
            {canApprove && (detail?.status ?? order.status) === "pending_approval" && (
              <>
                <button onClick={() => doAction(`${API}/sales-orders/${order.id}/approval`, { action: "approve" })}
                  disabled={actionLoading} style={{
                    padding: "8px 16px", borderRadius: 8, border: "none",
                    background: "linear-gradient(135deg,#059669,#10b981)", color: "#fff",
                    cursor: "pointer", fontSize: 12, fontWeight: 700,
                  }}>✅ {l.salesOrders?.approve}</button>
                <button onClick={() => setShowReject(p => !p)} style={{
                  padding: "8px 14px", borderRadius: 8,
                  border: `1px solid ${t.accent.red}44`, background: t.accent.redBg,
                  color: t.accent.red, cursor: "pointer", fontSize: 12,
                }}>✗ {l.salesOrders?.reject}</button>
              </>
            )}
            {canApprove && (detail?.status ?? order.status) === "approved" && (
              <button onClick={handleStartProduction} disabled={actionLoading} style={{
                padding: "8px 16px", borderRadius: 8, border: "none",
                background: actionLoading ? "#6b7280" : "linear-gradient(135deg,#7c3aed,#a78bfa)",
                color: "#fff",
                cursor: actionLoading ? "not-allowed" : "pointer",
                fontSize: 12, fontWeight: 700,
                opacity: actionLoading ? 0.7 : 1,
              }}>
                {actionLoading
                  ? `⏳ ${l.salesOrders?.processing}`
                  : `🏭 ${l.salesOrders?.startProduction}`}
              </button>
            )}
            {canCancel && !["cancelled", "completed", "shipped"].includes(order.status) && (
              <button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  style={{
                padding: "8px 14px", borderRadius: 8,
                border: `1px solid ${t.accent.red}44`, background: "none",
                color: t.accent.red, cursor: "pointer", fontSize: 12,
              }}>🗑️ {l.salesOrders?.cancel}</button>
            )}
            <button onClick={onClose} style={{
              padding: "8px 14px", borderRadius: 8,
              border: `1px solid ${t.border.input}`, background: t.bg.toggle,
              color: t.text.secondary, cursor: "pointer", fontSize: 12,
            }}>✕</button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 28, background: t.bg.modal }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: t.text.muted }}>
              {l.salesOrders?.loading}
            </div>
          ) : !detail ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: t.text.placeholder }}>
              {l.salesOrders?.detailError}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              {/* Reddetme formu */}
              {showReject && (
                <div style={{
                  padding: 16, background: t.accent.redBg,
                  border: `1px solid ${t.accent.red}44`, borderRadius: 12,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.accent.red, marginBottom: 10 }}>
                    {l.salesOrders?.rejectionReason}
                  </div>
                  <input type="text" value={rejectionReason}
                    onChange={e => setRejectionReason(e.target.value)}
                    placeholder={l.salesOrders?.rejectionPlaceholder}
                    style={{ ...inputStyle, marginBottom: 10 }} />
                  <button onClick={() => doAction(`${API}/sales-orders/${order.id}/approval`,
                    { action: "reject", rejection_reason: rejectionReason })} style={{
                    padding: "8px 16px", borderRadius: 8, border: "none",
                    background: t.accent.red, color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700,
                  }}>{l.salesOrders?.reject}</button>
                </div>
              )}

              {detail.rejection_reason && (
                <div style={{
                  padding: "10px 16px", background: t.accent.redBg,
                  border: `1px solid ${t.accent.red}44`, borderRadius: 10,
                  fontSize: 13, color: t.accent.red,
                }}>
                  ✗ {l.salesOrders?.rejectionReasonLabel}: <strong>{detail.rejection_reason}</strong>
                </div>
              )}

              {/* Özet */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 18 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 12, textTransform: "uppercase" }}>
                    📋 {l.salesOrders?.orderInfo}
                  </div>
                  {[
                    [l.salesOrders?.orderNo, detail.order_no],
                    [l.salesOrders?.customer, detail.customer_name],
                    [l.salesOrders?.customerType, detail.customer_type === "foreign" ? (l.salesOrders?.foreign) : (l.salesOrders?.domestic)],
                    [l.salesOrders?.orderDate, fmt(detail.order_date)],
                    [l.salesOrders?.deliveryDate, fmt(detail.delivery_date)],
                    [l.salesOrders?.createdBy, detail.created_by],
                    [l.salesOrders?.approvedBy, detail.approved_by || "—"],
                    [l.salesOrders?.notes, detail.notes || "—"],
                  ].map(([k, v]) => (
                    <div key={k} style={{
                      display: "flex", justifyContent: "space-between",
                      padding: "7px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13,
                    }}>
                      <span style={{ color: t.text.label }}>{k}</span>
                      <span style={{ color: t.text.primary, fontWeight: 500 }}>{v || "—"}</span>
                    </div>
                  ))}
                </div>
                <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 18 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 12, textTransform: "uppercase" }}>
                    📊 {l.salesOrders?.productionStatus}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {(() => {
                      const orderItems = detail.items || [];
                      const totalQty = orderItems.reduce((s, i) => s + Number(i.quantity || 0), 0);
                      const qcPassedQty = orderItems.reduce((s, i) => s + Number(i.kk_passed || 0), 0);
                      const waitingQty = Math.max(totalQty - qcPassedQty, 0);
                      return [
                        { label: l.salesOrders?.orderItemCount, value: orderItems.length, color: t.accent.blue },
                        { label: l.salesOrders?.orderQuantity, value: totalQty, color: t.accent.yellow },
                        { label: l.salesOrders?.qcPassedQty, value: `${qcPassedQty}/${totalQty}`, color: t.accent.green },
                        { label: l.salesOrders?.waitingQty, value: waitingQty, color: t.text.muted },
                      ];
                    })().map(({ label, value, color }) => (
                      <div key={label} style={{ background: t.bg.tag, borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
                        <div style={{ fontSize: 10, color: t.text.muted, textTransform: "uppercase", marginTop: 2 }}>{label}</div>
                      </div>
                    ))}
                  </div>
                  {detail.items?.length > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 12, color: t.text.muted }}>
                        <span>{l.salesOrders?.qcCompletion}</span>
                        <span style={{ fontWeight: 700, color: t.accent.green }}>
                          {(() => { const total = detail.items.reduce((s,i) => s + (i.quantity||0), 0); const passed = detail.items.reduce((s,i) => s + (i.kk_passed||0), 0); return total > 0 ? Math.round(passed/total*100) : 0; })()}%
                        </span>
                      </div>
                      <div style={{ height: 8, background: t.bg.tag, borderRadius: 4, overflow: "hidden" }}>
                        <div style={{
                          height: "100%", borderRadius: 4, background: t.accent.green,
                          width: `${(() => { const total = detail.items.reduce((s,i) => s + (i.quantity||0), 0); const passed = detail.items.reduce((s,i) => s + (i.kk_passed||0), 0); return total > 0 ? Math.round(passed/total*100) : 0; })()}%`,
                        }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* FATURA GEÇMİŞİ */}
              {invoiceHistory.length > 0 && (
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden" }}>
                  <div style={{ padding: "14px 20px", borderBottom: `1px solid ${t.border.default}`, fontSize: 14, fontWeight: 700, color: t.text.primary }}>
                    🧾 {l.salesOrders?.invoiceHistory} ({invoiceHistory.length})
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: t.bg.theadRow }}>
                        {[
                          l.salesOrders?.invoiceNo,
                          l.salesOrders?.date,
                          l.salesOrders?.quantity,
                          l.salesOrders?.amount,
                          l.salesOrders?.issuedBy,
                        ].map(h => (
                          <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceHistory.map((inv, i) => (
                        <tr key={inv.id} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                          <td style={{ padding: "9px 12px", fontSize: 13, fontWeight: 700, color: t.accent.blue }}>{inv.invoice_no}</td>
                          <td style={{ padding: "9px 12px", fontSize: 12, color: t.text.secondary }}>{inv.created_at ? new Date(inv.created_at).toLocaleDateString(getLocale()) : "—"}</td>
                          <td style={{ padding: "9px 12px", fontSize: 13, fontWeight: 600, color: t.text.primary }}>{inv.quantity || "—"}</td>
                          <td style={{ padding: "9px 12px", fontSize: 12, color: t.accent.green }}>{inv.total ? money(inv.total) : "—"}</td>
                          <td style={{ padding: "9px 12px", fontSize: 12, color: t.text.muted }}>{inv.created_by || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* FATURA & SEVKİYAT BİLGİLERİ */}
              <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 20, marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>
                  🧾 {l.salesOrders?.invoiceAndShipping}
                </div>

                {detail.invoice_status === 'invoiced' && !invoiceOpen ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", background: "#22c55e11", border: "1px solid #22c55e33", borderRadius: 10 }}>
                      <span style={{ fontSize: 16 }}>✅</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#22c55e" }}>
                          {l.salesOrders?.invoiced}
                        </div>
                        <div style={{ fontSize: 11, color: t.text.muted }}>
                          {detail.invoice_no || detail.invoice_date || detail.invoice_by ? (
                            <>
                              {l.salesOrders?.invoiceNo}: <strong>{detail.invoice_no || "—"}</strong> ·{" "}
                              {l.salesOrders?.date}: <strong>{detail.invoice_date ? new Date(detail.invoice_date).toLocaleDateString(getLocale()) : "—"}</strong> ·{" "}
                              {l.salesOrders?.issuedBy}: <strong>{detail.invoice_by || "—"}</strong>
                            </>
                          ) : (
                            <span>Fatura bilgisi bekleniyor</span>
                          )}
                        </div>
                      </div>
                      {canManageInvoice && detail.status !== 'completed' && detail.status !== 'shipped' && (
                        <button onClick={() => canManageInvoice && setInvoiceOpen(true)} style={{ padding:"6px 12px", borderRadius:8, border:"1px solid #22c55e44", background:"#22c55e11", color:"#22c55e", cursor:"pointer", fontSize:11, fontWeight:600 }}>
                          + {l.salesOrders?.newInvoice}
                        </button>
                      )}
                    </div>
                    {detail.delivery_note_no && (
                      <div style={{ fontSize: 12, color: t.text.secondary, padding: "8px 16px", background: t.bg.toggle, borderRadius: 8 }}>
                        📋 {l.salesOrders?.deliveryNoteNo}: <strong>{detail.delivery_note_no}</strong>
                        {detail.cargo_company && ` · 🚚 ${detail.cargo_company}`}
                        {detail.cargo_tracking_no && ` · ${l.salesOrders?.tracking}: ${detail.cargo_tracking_no}`}
                      </div>
                    )}
                    {detail.customer_confirmed ? (
                      <div style={{ fontSize: 12, color: "#22c55e", padding: "8px 16px", background: "#22c55e11", borderRadius: 8 }}>
                        ✅ {l.salesOrders?.customerConfirmed} · {detail.customer_confirmed_at ? new Date(detail.customer_confirmed_at).toLocaleDateString(getLocale()) : ""}
                      </div>
                    ) : (
                      <button onClick={async () => {
                        await fetch(`${API}/sales-orders/${detail.id}/invoice`, {
                          method: "PATCH", headers: headers(),
                          body: JSON.stringify({ action: "customer_confirm" })
                        });
                        onRefresh();
                      }} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#22c55e,#16a34a)", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, width: "fit-content" }}>
                        ✅ {l.salesOrders?.customerDelivered}
                      </button>
                    )}
                  </div>
                ) : canManageInvoice ? (
                  <InvoiceForm orderId={detail.id} onSaved={() => { onRefresh(); setInvoiceOpen(false); }} t={t} />
                ) : (
                  <div style={{ padding: "10px 16px", background: t.bg.tag, border: `1px solid ${t.border.subtle}`, borderRadius: 8, fontSize: 12, color: t.text.secondary }}>
                    Fatura durumu: Kesilmedi
                  </div>
                )}
              </div>

              {/* Kalemler tablosu */}
              <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden" }}>
                <div style={{ padding: "14px 20px", borderBottom: `1px solid ${t.border.default}`, fontSize: 14, fontWeight: 700, color: t.text.primary }}>
                  📦 {l.salesOrders?.orderItems}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: t.bg.theadRow }}>
                      {[
                        l.salesOrders?.product,
                        l.salesOrders?.code,
                        l.salesOrders?.quantity,
                        l.salesOrders?.unitPrice,
                        l.salesOrders?.total,
                        l.salesOrders?.workOrder,
                        l.salesOrders?.machine,
                        l.salesOrders?.operator,
                        l.salesOrders?.qcShort,
                        l.salesOrders?.status,
                      ].map(h => (
                        <th key={h} style={{
                          padding: "10px 12px", textAlign: "left", fontSize: 10,
                          fontWeight: 700, color: t.text.label, textTransform: "uppercase", whiteSpace: "nowrap",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.items || []).map((item, i) => {
                      const qcCfg = item.kk_passed > 0 && item.kk_failed === 0
                        ? { label: `✅ ${l.salesOrders?.qcPass}`, color: t.accent.green }
                        : item.kk_passed > 0 && item.kk_failed > 0
                        ? { label: `⚠ ${l.salesOrders?.qcPartial}`, color: t.accent.yellow }
                        : item.kk_failed > 0
                        ? { label: `✗ ${l.salesOrders?.qcFail}`, color: t.accent.red }
                        : { label: l.salesOrders?.waiting, color: t.text.muted };

                      return (
                        <tr key={item.id} style={{
                          borderTop: `1px solid ${t.border.subtle}`,
                          background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row,
                        }}>
                          <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600, color: t.text.primary }}>{item.product_name}</td>
                          <td style={{ padding: "10px 12px", fontSize: 11, color: t.text.muted }}>{item.product_code}</td>
                          <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: t.accent.blue }}>{item.quantity} {item.unit === "adet" ? l.salesOrders?.unitPcs : item.unit}</td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.secondary }}>{money(item.unit_price)}</td>
                          <td style={{ padding: "10px 12px", fontSize: 12, fontWeight: 600, color: t.accent.green }}>
                            {money((item.unit_price || 0) * (item.quantity || 0))}
                          </td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: item.work_order_no ? t.accent.purple : t.text.placeholder }}>
                            {item.work_order_no || "—"}
                          </td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{item.machine_id || "—"}</td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{item.assigned_to || "—"}</td>
                          <td style={{ padding: "10px 12px" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: qcCfg.color }}>{qcCfg.label}</div>
                            {item.kk_passed > 0 && (
                              <div style={{ fontSize: 10, color: t.text.muted, marginTop: 2 }}>
                                ✅ {item.kk_passed} {l.salesOrders?.passed} {item.kk_failed > 0 ? `· ❌ ${item.kk_failed} ${l.salesOrders?.scrap}` : ""}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: ORDER_STATUS[item.status]?.color || t.text.muted }}>
                              {ORDER_STATUS[item.status]?.label || item.status}
                            </div>
                            {item.shipped_quantity > 0 && (
                              <div style={{ fontSize: 10, marginTop: 2 }}>
                                <span style={{ color: "#22c55e" }}>🚚 {item.shipped_quantity} {l.salesOrders?.shipped}</span>
                                {item.quantity - item.shipped_quantity > 0 && (
                                  <span style={{ color: "#f97316", marginLeft: 4 }}>· {item.quantity - item.shipped_quantity} {l.salesOrders?.remaining}</span>
                                )}
                              </div>
                            )}
                            {item.quantity - (item.shipped_quantity || 0) > 0 && item.status === 'shipped' && (
                              <div style={{ fontSize: 10, color: "#ef4444", marginTop: 2 }}>⚠️ {l.salesOrders?.missingShipment}</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Üretim sonucu */}
              {productionResult && (
                <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary, marginBottom: 14 }}>
                    🏭 {l.salesOrders?.productionPlanResult}
                  </div>
                  {productionResult.all_from_stock && (
                    <div style={{
                      padding: "10px 14px", background: t.accent.greenBg,
                      border: `1px solid ${t.accent.green}44`, borderRadius: 8,
                      color: t.accent.green, fontSize: 13, fontWeight: 600, marginBottom: 12,
                    }}>✅ {l.salesOrders?.allFromStock}</div>
                  )}
                  {productionResult.results?.map((r, i) => (
                    <div key={i} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "10px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13,
                    }}>
                      <div>
                        <span style={{ fontWeight: 600, color: t.text.primary }}>{r.product}</span>
                        <span style={{ fontSize: 11, color: t.text.muted, marginLeft: 8 }}>{r.message}</span>
                      </div>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                        color: r.action === "stock" ? t.accent.green : r.action === "stock_partial" ? t.accent.yellow : t.accent.blue,
                        background: r.action === "stock" ? t.accent.greenBg : r.action === "stock_partial" ? t.accent.yellowBg : t.accent.blueBg,
                      }}>
                        {r.action === "stock"
                          ? `✅ ${l.salesOrders?.fromStock}`
                          : r.action === "stock_partial"
                            ? `⚡ ${l.salesOrders?.mixed}`
                            : `🏭 ${r.work_order}`}
                      </span>
                    </div>
                  ))}
                  <button onClick={onClose} style={{
                    marginTop: 14, padding: "8px 20px", borderRadius: 8, border: "none",
                    background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                    color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600,
                  }}>{l.salesOrders?.ok}</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── FATURA FORMU ─────────────────────────────────────────────────────────
const InvoiceForm = ({ orderId, onSaved, t }) => {
  const { t: l } = useLanguage();
  const [form, setForm] = useState({
    invoice_no: "", invoice_date: new Date().toISOString().slice(0, 10),
    delivery_note_no: "", cargo_company: "", cargo_tracking_no: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.invoice_no) return setError(l.salesOrders?.errInvoiceNoRequired);
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/sales-orders/${orderId}/invoice`, {
        method: "PATCH", headers: headers(),
        body: JSON.stringify({ action: "invoice", ...form })
      });
      if (!res.ok) throw new Error((await res.json()).detail || (l.salesOrders?.errGeneric));
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inp = { width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const Lbl = ({ children }) => <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{children}</label>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ padding: "10px 14px", background: "#f9731611", border: "1px solid #f9731633", borderRadius: 8, fontSize: 12, color: "#f97316" }}>
        ⚠️ {l.salesOrders?.invoiceWarning}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <div>
          <Lbl>{l.salesOrders?.invoiceNo} *</Lbl>
          <input value={form.invoice_no} onChange={e => set("invoice_no", e.target.value)} placeholder="FTR-2026-0001" style={inp} />
        </div>
        <div>
          <Lbl>{l.salesOrders?.invoiceDate}</Lbl>
          <input type="date" value={form.invoice_date} onChange={e => set("invoice_date", e.target.value)} style={inp} />
        </div>
        <div>
          <Lbl>{l.salesOrders?.deliveryNoteNo}</Lbl>
          <input value={form.delivery_note_no} onChange={e => set("delivery_note_no", e.target.value)} placeholder="İRS-2026-0001" style={inp} />
        </div>
        <div>
          <Lbl>{l.salesOrders?.cargoCompany}</Lbl>
          <input value={form.cargo_company} onChange={e => set("cargo_company", e.target.value)} placeholder={l.salesOrders?.cargoPlaceholder} style={inp} />
        </div>
        <div>
          <Lbl>{l.salesOrders?.trackingNo}</Lbl>
          <input value={form.cargo_tracking_no} onChange={e => set("cargo_tracking_no", e.target.value)} placeholder={l.salesOrders?.trackingPlaceholder} style={inp} />
        </div>
      </div>
      {error && <div style={{ padding: "8px 12px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 12 }}>⚠️ {error}</div>}
      <button onClick={handleSave} disabled={loading} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 700, width: "fit-content" }}>
        {loading
          ? (l.salesOrders?.saving)
          : `🧾 ${l.salesOrders?.markAsInvoiced}`}
      </button>
    </div>
  );
};

// ─── ANA SAYFA ────────────────────────────────────────────────────────────
export default function SalesOrders() {
  const { t } = useTheme();
  const { t: l } = useLanguage();
  const ORDER_STATUS = getOrderStatus();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [stats, setStats] = useState({});
  const userRole = localStorage.getItem("role") || "operator";
  const currentUser = localStorage.getItem("username") || "";

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/sales-orders`, { headers: headers() });
      if (!res.ok) throw new Error(l.salesOrders?.errOrdersFetch);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setOrders(list);
      setStats({
        total: list.length,
        draft: list.filter(o => o.status === "draft").length,
        inProd: list.filter(o => o.status === "in_production").length,
        ready: list.filter(o => o.status === "ready").length,
        completed: list.filter(o => ["completed","shipped"].includes(o.status)).length,
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [l]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const filtered = orders.filter(o => {
    const matchSearch = !search ||
      (o.order_no || "").toLowerCase().includes(search.toLowerCase()) ||
      (o.customer_name || "").toLowerCase().includes(search.toLowerCase());
    return matchSearch && (filterStatus === "all" || o.status === filterStatus);
  });

  const pagedOrders = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const selectStyle = {
    padding: "10px 14px", background: t.bg.input, border: `1px solid ${t.border.input}`,
    borderRadius: 10, color: t.text.primary, fontSize: 13, outline: "none", cursor: "pointer",
  };

  return (
    <div id="sales-top" style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@keyframes sf-spin { to{transform:rotate(360deg)} }`}</style>
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0 }}>
              🛒 {l.salesOrders?.title}
            </h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>
              {l.salesOrders?.subtitle}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={fetchOrders} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>🔄</button>
            <button onClick={() => {
              const hdrs = [
                l.salesOrders?.orderNo,
                l.salesOrders?.customer,
                l.salesOrders?.customerType,
                l.salesOrders?.amount,
                l.salesOrders?.status,
                l.salesOrders?.createdBy,
                l.salesOrders?.date,
              ];
              const rows = orders.map(o => [
                o.order_no, o.customer_name||o.customer, o.order_type||"—",
                o.total_amount||0, o.status, o.created_by||"—",
                o.created_at ? new Date(o.created_at).toLocaleDateString(getLocale()) : "—"
              ]);
              const csv = [hdrs, ...rows].map(r => r.map(v => `"${String(v||"").replace(/"/g,'""')}"`).join(";")).join("\n");
              const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = `sales_orders_${new Date().toISOString().slice(0,10)}.csv`;
              a.click();
            }} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>📥 Excel</button>
            <button onClick={() => setShowNew(true)} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              + {l.salesOrders?.newOrder}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { label: l.salesOrders?.statTotal, value: stats.total || 0, color: t.accent.blue, icon: "📋" },
            { label: l.salesOrders?.statusDraft, value: stats.draft || 0, color: t.accent.yellow, icon: "⏳" },
            { label: l.salesOrders?.statusInProduction, value: stats.inProd || 0, color: t.accent.purple, icon: "🏭" },
            { label: l.salesOrders?.statusReady, value: stats.ready || 0, color: t.accent.green, icon: "✅" },
            { label: l.salesOrders?.statusCompleted, value: stats.completed || 0, color: t.text.muted, icon: "📦" },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, minWidth: 130, background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "14px 18px", display: "flex", alignItems: "center", gap: 12, boxShadow: t.shadow.card }}>
              <span style={{ fontSize: 22 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 600 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
          <input
            placeholder={`🔍 ${l.salesOrders?.searchPlaceholder}`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...selectStyle, flex: 1, minWidth: 220 }} />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="all">{l.salesOrders?.allStatuses}</option>
            {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
            <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`, borderTopColor: t.accent.blue, borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
            <span style={{ color: t.text.muted }}>{l.salesOrders?.loading}</span>
          </div>
        ) : error ? (
          <div style={{ padding: 32, textAlign: "center", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 14, color: t.accent.red }}>⚠️ {error}</div>
        ) : (
          <div style={{
            background: t.bg.card,
            border: `1px solid ${t.border.card}`,
            borderRadius: 14,
            overflowX: "auto",
            overflowY: "hidden",
            boxShadow: t.shadow.card
          }}>
            <table style={{
              width: "100%",
              minWidth: 1180,
              borderCollapse: "collapse"
            }}>
              <thead>
                <tr style={{ background: t.bg.theadRow }}>
                  {[
                    l.salesOrders?.orderNo,
                    l.salesOrders?.customer,
                    l.salesOrders?.customerType,
                    l.salesOrders?.salesRep,
                    l.salesOrders?.salesRegion,
                    l.salesOrders?.itemCount,
                    l.salesOrders?.deliveryDate,
                    l.salesOrders?.createdBy,
                    l.salesOrders?.shipping,
                    l.salesOrders?.status,
                    l.salesOrders?.action,
                  ].map(h => (
                    <th
                      key={h}
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontSize: 11,
                        fontWeight: 700,
                        color: t.text.label,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        whiteSpace: "nowrap",
                        ...(h === l.salesOrders?.action
                          ? {
                              minWidth: 96,
                              width: 96,
                              position: "sticky",
                              right: 0,
                              zIndex: 3,
                              background: t.bg.theadRow,
                              boxShadow: "-8px 0 12px rgba(0,0,0,0.10)"
                            }
                          : {}),
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={11} style={{ textAlign: "center", color: t.text.placeholder, padding: 60 }}>
                    {orders.length === 0
                      ? (l.salesOrders?.noOrders)
                      : (l.salesOrders?.noMatch)}
                  </td></tr>
                ) : pagedOrders.map((o, i) => (
                  <tr key={o.id} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                    <td style={{ padding: "13px 16px", fontWeight: 700, color: t.accent.blue, fontSize: 13 }}>{o.order_no}</td>
                    <td style={{ padding: "13px 16px", fontSize: 13, fontWeight: 600, color: t.text.primary }}>{o.customer_name}</td>
                    <td style={{ padding: "13px 16px" }}>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 700, color: o.customer_type === "foreign" ? "#a78bfa" : "#60a5fa", background: o.customer_type === "foreign" ? "rgba(167,139,250,0.15)" : "rgba(96,165,250,0.15)" }}>
                        {o.customer_type === "foreign"
                          ? (l.salesOrders?.foreign)
                          : (l.salesOrders?.domestic)}
                      </span>
                    </td>
                    <td style={{ padding: "13px 16px", fontSize: 12, color: t.text.muted }}>
                      {o.sales_person || o.sales_rep || "—"}
                    </td>
                    <td style={{ padding: "13px 16px" }}>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 700, color: (o.sales_region || o.order_type) === "foreign" ? "#a78bfa" : "#60a5fa", background: (o.sales_region || o.order_type) === "foreign" ? "rgba(167,139,250,0.15)" : "rgba(96,165,250,0.15)" }}>
                        {(o.sales_region || o.order_type) === "foreign"
                          ? l.salesOrders?.exportRegion
                          : l.salesOrders?.domesticRegion}
                      </span>
                    </td>
                    <td style={{ padding: "13px 16px", fontWeight: 700, color: t.accent.yellow, fontSize: 13 }}>
                      {o.item_count || 0} {l.salesOrders?.item}
                      {o.product_name && <div style={{fontSize:11,color:t.text.muted,marginTop:2}}>{o.item_count === 1 ? o.product_name : l.salesOrders?.differentProducts?.replace("{count}", o.item_count)}</div>}
                    </td>
                    <td style={{ padding: "13px 16px", fontSize: 12, color: t.text.muted }}>{fmt(o.delivery_date)}</td>
                    <td style={{ padding: "13px 16px", fontSize: 12, color: t.text.muted }}>{o.created_by || "—"}</td>
                    <td style={{ padding: "13px 16px", fontSize: 12 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {(o.shipped_qty || 0) > 0 && (
                          <div style={{ fontSize: 12, color: t.accent.green, fontWeight: 700 }}>
                            ✅ {o.shipped_qty} {l.salesOrders?.shipped}
                          </div>
                        )}
                        {(o.ready_to_ship_qty || 0) > 0 && (
                          <div style={{ fontSize: 12, color: t.accent.blue, fontWeight: 700 }}>
                            📦 {o.ready_to_ship_qty || 0} {l.salesOrders?.readyToShip}
                          </div>
                        )}
                        {(o.in_production_qty || 0) > 0 && (
                          <div style={{ fontSize: 11, color: t.accent.yellow }}>
                            🏭 {o.in_production_qty} {l.salesOrders?.inProductionLabel}
                          </div>
                        )}
                        {!["rejected", "cancelled"].includes(o.status) && (o.shipped_qty || 0) === 0 && (o.ready_to_ship_qty || 0) === 0 && (o.in_production_qty || 0) === 0 && (
                          <div style={{ fontSize: 11, color: t.text.muted }}>
                            ⏳ {o.total_qty || 0} {l.salesOrders?.waitingLabel}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "13px 16px" }}><StatusBadge status={o.status} /></td>
                    <td style={{
                      padding: "13px 16px",
                      minWidth: 96,
                      width: 96,
                      whiteSpace: "nowrap",
                      position: "sticky",
                      right: 0,
                      zIndex: 2,
                      background: t.bg.card,
                      boxShadow: "-8px 0 12px rgba(0,0,0,0.10)"
                    }}>
                      <button
                        onClick={() => setSelected(o)}
                        style={{
                          padding: "6px 14px",
                          borderRadius: 8,
                          cursor: "pointer",
                          fontSize: 12,
                          border: `1px solid ${t.accent.blueBorder}`,
                          background: t.accent.blueBg,
                          color: t.accent.blue,
                          fontWeight: 600,
                          whiteSpace: "nowrap"
                        }}
                      >
                        {l.salesOrders?.detail}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && (
          <div style={{ marginTop: 16, color: t.text.placeholder, fontSize: 12, textAlign: "right" }}>
            {filtered.length} / {orders.length} {l.salesOrders?.orderCount}
          </div>
        )}
      </div>

      {showNew && (
        <OrderFormModal t={t} onClose={() => setShowNew(false)} onSaved={() => {
          setShowNew(false);
          fetchOrders();
          setTimeout(() => document.getElementById('sales-top')?.scrollIntoView(), 100);
        }} />
      )}
      {!loading && !error && filtered.length > 0 && (
        <PaginationBar
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalRecords={filtered.length}
          label={l.salesOrders?.recordUnit}
          t={t}
        />
      )}

      {selected && (
        <OrderDetailModal order={selected} t={t} userRole={userRole} currentUser={currentUser}
          onClose={() => setSelected(null)} onRefresh={() => { fetchOrders(); setSelected(null); }} />
      )}
    </div>
  );
}
